/* eslint-disable @eslint-react/no-array-index-key -- static skeleton placeholders */
import { Fragment, HTMLAttributes } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Generic Loading Skeleton Component
 * Provides consistent loading states across the application
 * Eliminates duplication of loading skeleton implementations
 */

// ============================================================================
// BASE SKELETON
// ============================================================================

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The shape of the skeleton
   */
  variant?: 'rectangle' | 'circle' | 'text';

  /**
   * Width of the skeleton (can be number for pixels or string for any CSS value)
   */
  width?: number | string;

  /**
   * Height of the skeleton (can be number for pixels or string for any CSS value)
   */
  height?: number | string;

  /**
   * Whether to show animation
   */
  animate?: boolean;
}

function Skeleton({
  className,
  variant = 'rectangle',
  width,
  height,
  animate = true,
  style,
  ...props
}: SkeletonProps) {
  const variantClasses = {
    rectangle: 'rounded-md',
    circle: 'rounded-full',
    text: 'rounded h-4'
  };

  const computedStyle: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style
  };

  return (
    <div
      className={cn(
        'bg-muted',
        animate && 'animate-pulse',
        variantClasses[variant],
        className
      )}
      style={computedStyle}
      {...props}
    />
  );
}

// ============================================================================
// COMPOSITE SKELETONS
// ============================================================================

/**
 * Card Skeleton
 */
export interface CardSkeletonProps {
  showHeader?: boolean;
  showFooter?: boolean;
  lines?: number;
  className?: string;
}

export function CardSkeleton({
  showHeader = true,
  showFooter = false,
  lines = 3,
  className
}: CardSkeletonProps) {
  return (
    <div className={cn('space-y-4 rounded-lg border p-6', className)}>
      {showHeader && (
        <div className="space-y-2">
          <Skeleton variant="text" width="40%" />
          <Skeleton
            variant="text"
            width="60%"
            height={12}
            className="opacity-70"
          />
        </div>
      )}

      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={i === lines - 1 ? '80%' : '100%'}
          />
        ))}
      </div>

      {showFooter && (
        <div className="flex gap-2 pt-4">
          <Skeleton width={80} height={32} />
          <Skeleton width={80} height={32} />
        </div>
      )}
    </div>
  );
}

/**
 * Table Skeleton
 */
export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true,
  className
}: TableSkeletonProps) {
  return (
    <div className={cn('rounded-lg border', className)}>
      {showHeader && (
        <div className="flex gap-4 border-b px-4 py-3">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton
              key={i}
              variant="text"
              width={`${100 / columns}%`}
              height={20}
            />
          ))}
        </div>
      )}

      <div>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex gap-4 border-b px-4 py-3 last:border-0"
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                variant="text"
                width={`${100 / columns}%`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * List Item Skeleton
 */
export interface ListItemSkeletonProps {
  showAvatar?: boolean;
  showActions?: boolean;
  lines?: number;
  className?: string;
}

export function ListItemSkeleton({
  showAvatar = true,
  showActions = false,
  lines = 2,
  className
}: ListItemSkeletonProps) {
  return (
    <div className={cn('flex items-center gap-4 p-4', className)}>
      {showAvatar && <Skeleton variant="circle" width={40} height={40} />}

      <div className="flex-1 space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={i === 0 ? '50%' : '75%'}
            height={i === 0 ? 16 : 14}
          />
        ))}
      </div>

      {showActions && (
        <div className="flex gap-2">
          <Skeleton width={32} height={32} />
          <Skeleton width={32} height={32} />
        </div>
      )}
    </div>
  );
}

/**
 * Form Skeleton
 */
export interface FormSkeletonProps {
  fields?: number;
  showLabels?: boolean;
  showSubmit?: boolean;
  className?: string;
}

export function FormSkeleton({
  fields = 3,
  showLabels = true,
  showSubmit = true,
  className
}: FormSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          {showLabels && <Skeleton variant="text" width="30%" height={14} />}
          <Skeleton height={40} />
        </div>
      ))}

      {showSubmit && (
        <div className="flex gap-2 pt-4">
          <Skeleton width={100} height={40} />
          <Skeleton width={100} height={40} />
        </div>
      )}
    </div>
  );
}

/**
 * Avatar Skeleton
 */
export interface AvatarSkeletonProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AvatarSkeleton({
  size = 'md',
  className
}: AvatarSkeletonProps) {
  const sizes = {
    sm: 32,
    md: 40,
    lg: 56
  };

  return (
    <Skeleton
      variant="circle"
      width={sizes[size]}
      height={sizes[size]}
      className={className}
    />
  );
}

/**
 * Stats Card Skeleton
 */
export interface StatsCardSkeletonProps {
  className?: string;
}

export function StatsCardSkeleton({ className }: StatsCardSkeletonProps) {
  return (
    <div className={cn('space-y-2 rounded-lg border p-6', className)}>
      <Skeleton variant="text" width="50%" height={14} />
      <Skeleton variant="text" width="80%" height={32} />
      <Skeleton variant="text" width="60%" height={12} className="opacity-70" />
    </div>
  );
}

// ============================================================================
// SKELETON GROUP
// ============================================================================

/**
 * Skeleton Group - for rendering multiple skeletons
 */
export interface SkeletonGroupProps {
  count: number;
  children: ReactElement;
  className?: string;
}

export function SkeletonGroup({
  count,
  children,
  className
}: SkeletonGroupProps) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <Fragment key={i}>{children}</Fragment>
      ))}
    </div>
  );
}

// ============================================================================
// LOADING CONTAINER
// ============================================================================

/**
 * Loading Container - wraps content with loading state
 */
export interface LoadingContainerProps {
  isLoading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  className?: string;
}

export function LoadingContainer({
  isLoading,
  skeleton,
  children,
  className
}: LoadingContainerProps) {
  return <div className={className}>{isLoading ? skeleton : children}</div>;
}

export { Skeleton };
